// AxisProps.tsx, holds the component for X-AxisProps.tsx and Y-AxisProps.tsx
import React, { useState, useEffect } from 'react';
import InputNumber from '../../CommonFunctions/InputNumber';
import { MinMaxInput } from '../../Color/ColorScaleComponents/MinMaxInput';
import { ScaleTypeRadio } from '../../Color/ColorScaleComponents/ScaleTypeRadio';
import { useDispatch, useSelector } from 'react-redux';
import { setColorScaleOption } from '../../../../redux/ChartPoperties/ChartControlsActions';
import { RootState } from '../../../../redux';
import { useColorScale } from '../../Color/ColorScaleComponents/useColorScale';
import SwitchWithInput from '../../SwitchWithInput';
import MultipleSeries from './MultipleSeries';
import SeriesAxisOptions from './SeriesAxisOptions';
import YAxisFormat from '../../Format/YAxisFormat';
import XAxisFormat from '../../Format/XAxisFormat';
import SliderWithInput from '../../SliderWithInput';
import { Box } from '../../../DataViewer/Box';
import { Box as MuiBox } from '@mui/material';
interface PositionsProps {
  name?: string;
  value: string;
  type?: string;
}

interface AxisPropsComponentProps {
  propKey: string;
  chartType: string;
  axisType: 'x' | 'y';
  property: any;
  axisProps: any;
  updateAxisOptions: (axis: string, option: string, value: any, key?: string) => void;
  updateAxisMinMaxforScatter: (axisKey: string, value: any) => void;
  enableGrids: (propKey: string, option: string, value: any) => void;
  positions: PositionsProps[];
  axisOptions: PositionsProps[];
  iconColor?: string;
  backgroundColor?: string;
}

/**
 * AxisProps component handles the customization of axis properties such as labels, ticks, position, and grid settings.
 * It allows users to configure various options based on the chart type and axis type.
 * this component renders multiple series functionality for multibar, horizontal bar, line chart and area chart.
 */

const AxisProps: React.FC<AxisPropsComponentProps> = ({
  //state
  propKey,
  chartType,
  axisType,
  property,
  axisProps,
  positions,
  axisOptions,
  iconColor,
  backgroundColor,

  //dispatch
  updateAxisOptions,
  updateAxisMinMaxforScatter,
}) => {
  const dispatch = useDispatch();
  const chartControls = useSelector((state: RootState) => state.chartControls);

  const axisKey = `${axisType}Axis`;

  const scatterMinMax = property?.scatterChartMinMax || {};
  const scEnableMin = scatterMinMax[`${axisType}_enableMin`];
  const scEnableMax = scatterMinMax[`${axisType}_enableMax`];
  const scMinValue = scatterMinMax[`${axisType}_minValue`];
  const scMaxValue = scatterMinMax[`${axisType}_maxValue`];

  //   if both are enabled AND Min >= Max
  const scatterMinMaxError = scEnableMin && scEnableMax && Number(scMinValue) >= Number(scMaxValue);

  useEffect(() => {
    if (axisProps.showAxis === undefined) {
      updateAxisOptions(axisKey, 'showAxis', true);
    }
  }, [axisProps.showAxis, axisKey, updateAxisOptions]);

  const colorScale = chartControls.properties[propKey].colorScale;
  const selectedOption = colorScale.colorScaleType;

  const handleColorChange = (option: string, value: string | boolean) => {
    dispatch(setColorScaleOption(option, value, propKey));
  };

  const { checkMinMaxValue } = useColorScale();
  const heatmapMinMaxError =
    colorScale.enableMin &&
    colorScale.enableMax &&
    Number(colorScale.min) >= Number(colorScale.max);

  useEffect(() => {
    if (axisProps.position === 'hide') {
      // disable axis label
      if (axisProps.showLabel !== false) {
        updateAxisOptions(axisKey, 'showLabel', false);
      }
    }
  }, [axisProps.position, axisProps.showLabel, axisKey, propKey, updateAxisOptions]);

  const renderAxisOptions = () => {
    return axisOptions
      .filter((item) => item.value !== 'hide') // 👈 filter out the 'hide' option
      .map((item: PositionsProps) => {
        const isSelected = item.value === axisProps.position;

        const handleClick = () => {
          updateAxisOptions(axisKey, 'position', item.value);
        };

        return (
          <div
            key={item.value}
            className={isSelected ? 'radioButtonSelected' : 'radioButton'}
            onClick={handleClick}
            style={{
              backgroundColor: isSelected ? 'rgba(224, 224, 224, 1)' : 'white',
              cursor: isSelected ? 'auto' : 'pointer',
              fontWeight: isSelected ? '600' : 'normal',
              marginLeft: '0',
            }}
          >
            {item.type || item.name}
          </div>
        );
      });
  };

  return (
    <div>
      <div
        className="optionDescription"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          // paddingLeft: '0.45rem',
          marginTop: '0',
          marginBottom: '10px',
        }}
      >
        <div style={{ fontWeight: 600, color: iconColor ? iconColor : '#404040' }}>
          {chartType === 'scatterPlot'
            ? axisType === 'x'
              ? 'X'
              : 'Y'
            : ['horizontalBar', 'horizontalStacked'].includes(chartType)
              ? axisType === 'x'
                ? 'MEASURE'
                : 'DIMENSION'
              : axisType === 'x'
                ? 'DIMENSION'
                : 'MEASURE'}
          -AXIS
        </div>

        <MuiBox
          sx={{
            width: '50px',
            height: '24px',
            display: 'flex',
            transform: 'translateX(63px)',
            marginBottom: '9px',

            '@supports (-moz-appearance: none)': {
              transform: 'translateX(55px)',
            },
          }}
        >
          <SwitchWithInput
            isChecked={axisProps.showAxis ?? true}
            onSwitch={() => {
              if (
                (['line', 'area', 'multibar'].includes(chartType) && axisType === 'y') ||
                (['horizontalBar'].includes(chartType) && axisType === 'x')
              ) {
                const axisOptions = chartControls.properties[propKey].axisOptions;
                const keys = Object.keys(axisOptions[axisKey]);
                keys.forEach((key) => {
                  const newValue = !axisProps.showAxis;
                  updateAxisOptions(axisKey, 'showAxis', newValue, key);

                  if (!newValue) {
                    updateAxisOptions(
                      axisKey,
                      'prevPosition',
                      axisOptions[axisKey][key].position,
                      key,
                    );
                    updateAxisOptions(axisKey, 'position', 'hide', key);
                    updateAxisOptions(axisKey, 'showLabel', false, key);
                  } else {
                    updateAxisOptions(
                      axisKey,
                      'position',
                      axisOptions[axisKey][key].prevPosition || '',
                      key,
                    );
                    updateAxisOptions(axisKey, 'showLabel', true, key);
                    updateAxisOptions(axisKey, 'axisLine.lineStyle.color', '#d9d2d2ff', key);
                  }
                });
              } else {
                const newValue = !axisProps.showAxis;
                updateAxisOptions(axisKey, 'showAxis', newValue);

                if (!newValue) {
                  updateAxisOptions(axisKey, 'prevPosition', axisProps.position);
                  updateAxisOptions(axisKey, 'position', 'hide');
                  updateAxisOptions(axisKey, 'showLabel', false);
                } else {
                  updateAxisOptions(axisKey, 'position', axisProps.prevPosition || '');
                  updateAxisOptions(axisKey, 'showLabel', true);
                  updateAxisOptions(axisKey, 'axisLine.lineStyle.color', '#d9d2d2ff');
                }
              }
            }}
          />
        </MuiBox>
      </div>
      {axisProps.showAxis && (
        <>
          <div>
            <div
              className="optionDescription"
              style={{
                marginTop: '-10px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Font Size
            </div>
            <div style={{ marginBottom: '25px' }}>
              <SliderWithInput
                percent={false}
                sliderValue={axisProps.fontSize ?? 12}
                sliderMinMax={{ min: 8, max: 30, step: 1 }}
                changeValue={(value: number) => updateAxisOptions(axisKey, 'fontSize', value)}
              />
            </div>
          </div>

          {chartType === 'heatmap' && axisType === 'y' && (
            <div style={{ marginBottom: '10px' }}>
              <div
                className="optionDescription"
                style={{
                  paddingBottom: '0',
                  paddingTop: '0px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Set Min Max Values:
              </div>

              <div className="colorScaleContainer" style={{ marginBottom: '-10px' }}>
                <ScaleTypeRadio
                  iconColor={iconColor}
                  selectedOption={selectedOption}
                  onOptionChange={(value) => handleColorChange('colorScaleType', value)}
                />

                {selectedOption === 'Manual' && (
                  <div style={{ transform: 'TranslateY(-10px)' }}>
                    <div className="inputFieldContainer">
                      <div
                        className="optionDescription"
                        style={{
                          marginLeft: '-25px',
                          marginTop: '-40px',
                          whiteSpace: 'nowrap',
                          color: iconColor ? iconColor : '#404040',
                        }}
                      >
                        Min Value
                      </div>
                      <div
                        style={{
                          marginTop: '20px',
                          transform: 'TranslateX(-76px)',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{ width: '16px', height: '16px', marginRight: '8px' }}
                          checked={colorScale.enableMin}
                          onChange={() => handleColorChange('enableMin', !colorScale.enableMin)}
                        />
                        <MinMaxInput
                          value={colorScale.min}
                          onChange={(value) => handleColorChange('min', value)}
                          onBlur={() => checkMinMaxValue(colorScale.min, colorScale.max)}
                          disabled={!colorScale.enableMin}
                        />
                      </div>
                    </div>
                    <div className="inputFieldContainer">
                      <div
                        className="optionDescription"
                        style={{
                          marginLeft: '-25px',
                          marginTop: '-30px',
                          whiteSpace: 'nowrap',
                          color: iconColor ? iconColor : '#404040',
                        }}
                      >
                        Max Value
                      </div>
                      <div
                        style={{
                          marginTop: '20px',
                          transform: 'TranslateX(-78px)',
                          display: 'flex',
                          alignItems: 'center',
                          marginBottom: '-10px',
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{ width: '16px', height: '16px', marginRight: '8px' }}
                          checked={colorScale.enableMax}
                          onChange={() => handleColorChange('enableMax', !colorScale.enableMax)}
                        />
                        <MinMaxInput
                          value={colorScale.max}
                          onChange={(value) => handleColorChange('max', value)}
                          onBlur={() => checkMinMaxValue(colorScale.min, colorScale.max)}
                          disabled={!colorScale.enableMax}
                        />
                      </div>
                    </div>
                    {heatmapMinMaxError && (
                      <div
                        style={{
                          color: '#d32f2f',
                          fontSize: '12px',
                          marginTop: '15px',
                          marginBottom: '5px',
                          textAlign: 'left',
                          paddingLeft: '0px',
                        }}
                      >
                        Max value should be greater than Min Value
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Scatter Plot Min/Max */}
          {chartType === 'scatterPlot' && (
            <>
              <div style={{ transform: 'TranslateY(-3px)' }}>
                <div
                  className="optionDescription"
                  style={{
                    marginBottom: '7px',
                    marginTop: '-10px',
                    color: iconColor ? iconColor : '#404040',
                  }}
                >
                  Min Value
                </div>
                <div className="optionDescription" style={{ transform: 'TranslateY(-15px)' }}>
                  <input
                    type="checkbox"
                    style={{ width: '16px', height: '16px' }}
                    id={`enableMin-${axisType}`}
                    checked={property?.scatterChartMinMax?.[`${axisType}_enableMin`] ?? false}
                    onChange={() => {
                      const currentValue = property?.scatterChartMinMax?.[`${axisType}_enableMin`];

                      updateAxisMinMaxforScatter(`${axisType}_enableMin`, !currentValue);
                    }}
                  />
                  <InputNumber
                    value={property?.scatterChartMinMax?.[`${axisType}_minValue`] ?? ''}
                    updateValue={(value: number) =>
                      updateAxisMinMaxforScatter(`${axisType}_minValue`, value)
                    }
                    disabled={!property?.scatterChartMinMax?.[`${axisType}_enableMin`]}
                  />
                </div>

                {/* MAX VALUE SECTION */}
                <div style={{ transform: 'TranslateY(-10px)' }}>
                  <div
                    className="optionDescription"
                    style={{
                      marginTop: '0',
                      marginBottom: '7px',
                      color: iconColor ? iconColor : '#404040',
                    }}
                  >
                    Max Value
                  </div>
                  <div
                    className="optionDescription"
                    style={{ transform: 'TranslateY(-15px)', marginBottom: '-20px' }}
                  >
                    <input
                      type="checkbox"
                      style={{ width: '16px', height: '16px' }}
                      id={`enableMax-${axisType}`}
                      checked={property?.scatterChartMinMax?.[`${axisType}_enableMax`] ?? false}
                      onChange={() => {
                        updateAxisMinMaxforScatter(
                          `${axisType}_enableMax`,
                          !property?.scatterChartMinMax?.[`${axisType}_enableMax`],
                        );
                      }}
                    />
                    <InputNumber
                      value={property?.scatterChartMinMax?.[`${axisType}_maxValue`] ?? ''}
                      updateValue={(value: number) =>
                        updateAxisMinMaxforScatter(`${axisType}_maxValue`, value)
                      }
                      disabled={!property?.scatterChartMinMax?.[`${axisType}_enableMax`]}
                    />
                  </div>
                  {scatterMinMaxError && (
                    <div
                      style={{
                        color: '#d32f2f',
                        fontSize: '12px',
                        marginTop: '10px',
                        marginBottom: '10px',
                        textAlign: 'left',
                        paddingLeft: '10px',
                      }}
                    >
                      Max value should be greater than Min Value
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
          {(axisProps.showAxis &&
            ['line', 'area', 'multibar'].includes(chartType) &&
            axisType === 'y') ||
          (['horizontalBar'].includes(chartType) && axisType === 'x') ? (
            <MultipleSeries
              propKey={propKey}
              iconColor={iconColor}
              backgroundColor={backgroundColor}
            />
          ) : (
            <>
              {chartControls.properties[propKey].axisOptions[axisKey]['1'].position !== 'hide' && (
                <>
                  <div
                    className="optionDescription"
                    style={{
                      marginBottom: '0px',
                      marginTop: '-12px',
                      paddingLeft: '8px',
                      color: iconColor ? iconColor : '#404040',
                    }}
                  >
                    Position
                  </div>
                  <div className="radioButtons" style={{ marginTop: '0px', marginBottom: '15px' }}>
                    {renderAxisOptions()}
                  </div>
                </>
              )}

              {axisProps.showLabel &&
                ((!['line', 'area', 'multibar'].includes(chartType) && axisType !== 'x') ||
                  (!['horizontalBar'].includes(chartType) && axisType !== 'y')) && (
                  <SeriesAxisOptions
                    propKey={propKey}
                    seriesNumber="1"
                    axisType={axisType}
                    axisProps={axisProps}
                    positions={positions}
                    chartType={chartType}
                    iconColor={iconColor}
                    backgroundColor={backgroundColor}
                  />
                )}
            </>
          )}
          {!['heatmap'].includes(chartType) &&
            !['multibar', 'horizontalBar', 'line', 'area'].includes(chartType) && (
              <>
                {axisType === 'x' && chartType === 'scatterPlot' && (
                  <>
                    <div style={{ marginBottom: '15px', marginTop: '-10px' }}>
                      <XAxisFormat iconColor={iconColor} backgroundColor={backgroundColor} />
                    </div>
                  </>
                )}
                {axisType === 'y' && (
                  <>
                    <div style={{ marginBottom: '10px', marginTop: '-10px' }}>
                      <YAxisFormat iconColor={iconColor} backgroundColor={backgroundColor} />
                    </div>
                  </>
                )}
              </>
            )}
        </>
      )}
    </div>
  );
};

export default AxisProps;
